import { UserServiceClient } from 'fitbeat.api-clients';
import { getBoundedValue, StrengthAssessmentManager, UserProfile } from 'fitbeat.gym-core';
import {
  Gender,
  SmallBodyGroupType,
  smallBodyGroupTypesArray,
  StrengthRatioForMuscleGroups,
} from 'fitbeat.models';
import React, { useEffect, useRef, useState } from 'react';
import {
  PanResponder,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { connect } from 'react-redux';
import BodyGroupsImageFemale from '../assets/images/strengthAssessmentBodyGroupsFemale.svg';
import BodyGroupsImageMale from '../assets/images/strengthAssessmentBodyGroupsMale.svg';
import { Button, ButtonTypes, Icon } from '../components';
import { getApiConfig } from '../config/index';
import theme from '../theme';
import { SliderGauge } from '../ui/components/SliderGauge';
import { adjustHexByPerceptualScale } from '../utils/hexColourScaling';
import { AuthTokenBuilder } from '../utils';
import { Logger } from '../utils/Logger';
import { defaultScale, scaleText } from '../utils/scaling';
import { saveManualStrengthAssessment } from './actions';
import {
  selectIsAssessmentResultLoading,
  selectUserStrengthRatios,
  svgFillPropertyToBodyGroup,
} from './selectors';

const iconSize = defaultScale(25);
const sliderCardWidth = defaultScale(130);
const sliderCardMargin = defaultScale(18);

const getCurrentIndexOfBodyGroup = (currentBodyGroup: SmallBodyGroupType) => {
  const currentIndexOfBodyGroup = smallBodyGroupTypesArray.findIndex(
    (bodyGroup) => {
      return currentBodyGroup === bodyGroup;
    },
  );
  return currentIndexOfBodyGroup;
};

interface IStateProps {
  userStrengthRatios: StrengthRatioForMuscleGroups;
  isAssessmentResultLoading: boolean;
}

interface IDispatchProps {
  onSave(userStrengthRatios: StrengthRatioForMuscleGroups): Promise<void>;
}
interface IOwnProps {
  userProfile: UserProfile;
  getPreviousStep(): void;
}

type ComponentProps = IStateProps & IOwnProps & IDispatchProps;

export const ManualSelectionComponent: React.FC<ComponentProps> = ({
  getPreviousStep,
  userStrengthRatios,
  onSave,
  isAssessmentResultLoading,
  userProfile,
}) => {
  const [currentBodyGroup, setCurrentBodyGroup] = useState<SmallBodyGroupType>(
    smallBodyGroupTypesArray[0],
  );
  const [strengthValues, setStrengthValues] = useState(userStrengthRatios);
  const bodyGroupPositions = useRef({});
  const scrollViewRef = useRef<any>(null);
  const scrollOffset = useRef<number>(0);
  const scrollViewWidth = useRef<number | null>(null);
  const absoluteValueToRatioEquation = StrengthAssessmentManager.getStrengthRatioForPercentage(userProfile);
  const ratioToAbsoluteValueEquation = StrengthAssessmentManager.getPercentageForStrengthRatio(userProfile);
  const visibleStrengthValue = ratioToAbsoluteValueEquation(strengthValues[currentBodyGroup]);
  useEffect(() => {
    if (
      scrollViewRef.current &&
      typeof bodyGroupPositions.current[currentBodyGroup] === 'number' &&
      typeof scrollOffset.current === 'number' &&
      typeof scrollViewWidth.current === 'number'
    ) {
      const topLeftCorner = bodyGroupPositions.current[currentBodyGroup];
      const topRightCorner =
        bodyGroupPositions.current[currentBodyGroup] + sliderCardWidth;
      const dimensionsInViewLeft = scrollOffset.current;
      const dimensionsInViewRight =
        scrollViewWidth.current + dimensionsInViewLeft;
      const outOfView =
        topLeftCorner < dimensionsInViewLeft ||
        topRightCorner > dimensionsInViewRight;
      if (outOfView) {
        scrollViewRef.current.scrollTo({
          y: 0,
          x: bodyGroupPositions.current[currentBodyGroup] - 120,
        });
      }
    }
  }, [currentBodyGroup]);

  const strenghtValueSelected = (strengthValue) => {
    setStrengthValues((prev) => {
      return {
        ...prev,
        [currentBodyGroup]: absoluteValueToRatioEquation(strengthValue),
      };
    });
  };

  const incrementBodyGroup = () => {
    setCurrentBodyGroup((bodyGroup: SmallBodyGroupType) => {
      const currentIndexOfBodyGroup = getCurrentIndexOfBodyGroup(bodyGroup);
      const nextIndex =
        (currentIndexOfBodyGroup + 1) % smallBodyGroupTypesArray.length;
      return smallBodyGroupTypesArray[nextIndex];
    });
  };
  const decrementBodyGroup = () => {
    setCurrentBodyGroup((bodyGroup: SmallBodyGroupType) => {
      const currentIndexOfBodyGroup = getCurrentIndexOfBodyGroup(bodyGroup);
      const previousIndex =
        (currentIndexOfBodyGroup + smallBodyGroupTypesArray.length - 1) %
        smallBodyGroupTypesArray.length;
      return smallBodyGroupTypesArray[previousIndex];
    });
  };
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onPanResponderRelease: (evt, gestureState) => {
        if (gestureState.dx > 50) {
          decrementBodyGroup();
        } else if (gestureState.dx < -50) {
          incrementBodyGroup();
        }
      },
    }),
  ).current;

  const getCorrectColorForValue = (value: number) => {
    const roundedToTen = Math.round(value / 10) * 10;
    const boundedValue = getBoundedValue(roundedToTen, 10, 100);
    return adjustHexByPerceptualScale(theme.PRIMARY, (boundedValue / 10 - 1));
  };

  const getCorrectColorForProp = (prop) => {
    if (svgFillPropertyToBodyGroup[prop] === currentBodyGroup) {
      return getCorrectColorForValue(visibleStrengthValue);
    }
    return theme.GREY_LIGHT;
  };
  const sliderColor = getCorrectColorForValue(visibleStrengthValue);
  const BodyGroupImage = userProfile.gender === Gender.Male ? BodyGroupsImageMale : BodyGroupsImageFemale;

  return (
    <View style={styles.container}>
      <View style={{ flex: 1, justifyContent: 'center' }}>
        <View>
          <View>
            <ScrollView
              onLayout={(event) => {
                scrollViewWidth.current = event.nativeEvent.layout.width;
              }}
              horizontal
              onScroll={(event) => {
                scrollOffset.current = event.nativeEvent.contentOffset.x;
              }}
              ref={scrollViewRef}
              contentContainerStyle={{
                flexDirection: 'row',
                marginBottom: defaultScale(10),
              }}
              showsHorizontalScrollIndicator={false}
              snapToInterval={sliderCardWidth + sliderCardMargin + 1}
              decelerationRate='fast'
            >
              {smallBodyGroupTypesArray.map((bodyType) => {
                const isBodyGroupSelected = bodyType === currentBodyGroup;
                const backgroundColor = isBodyGroupSelected
                  ? theme.BLACK
                  : theme.WHITE;
                const color = isBodyGroupSelected ? theme.WHITE : theme.BLACK;
                return (
                  <TouchableOpacity
                    key={bodyType}
                    style={[styles.bodyGroupCard, { backgroundColor }]}
                    onLayout={(event) => {
                      const layout = event.nativeEvent.layout;
                      if (bodyGroupPositions.current) {
                        bodyGroupPositions.current[bodyType] = layout.x;
                      }
                    }}
                    onPress={() => {
                      if (currentBodyGroup === bodyType) {
                        return;
                      }
                      setCurrentBodyGroup(bodyType);
                    }}
                  >
                    <Text style={[styles.bodyGroupText, { color }]}>
                      {bodyType}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          </View>
          <View style={{ height: '70%' }} {...panResponder.panHandlers}>
            <View style={styles.chooseBodyGroupContainer}>
              <TouchableOpacity
                style={styles.iconContainer}
                onPress={decrementBodyGroup}
              >
                <Icon
                  name='ios-arrow-back'
                  color={theme.PRIMARY}
                  size={defaultScale(iconSize)}
                />
              </TouchableOpacity>
              <View style={styles.imageContainer}>
                <BodyGroupImage
                  height={'100%'}
                  width={'100%'}
                  fill={getCorrectColorForProp('fill')}
                  fillSecondary={getCorrectColorForProp('fillSecondary')}
                  color={getCorrectColorForProp('color')}
                  colorSecondary={getCorrectColorForProp('colorSecondary')}
                  colorFour={getCorrectColorForProp('colorFour')}
                  colorFive={getCorrectColorForProp('colorFive')}
                  colorSix={getCorrectColorForProp('colorSix')}
                  colorSeven={getCorrectColorForProp('colorSeven')}
                  colorEight={getCorrectColorForProp('colorEight')}
                />
              </View>
              <TouchableOpacity
                style={styles.iconContainer}
                onPress={incrementBodyGroup}
              >
                <Icon
                  name='ios-arrow-forward'
                  color={theme.PRIMARY}
                  size={defaultScale(iconSize)}
                />
              </TouchableOpacity>
            </View>
            <View style={styles.sliderContainer}>
              <SliderGauge
                onValueChange={strenghtValueSelected}
                initialValue={visibleStrengthValue}
                minimumValue={0}
                maximumValue={100}
                step={1}
                minimumTrackTintColor={sliderColor}
                maximumTrackTintColor={theme.GREY_LIGHT}
                style={{
                  height: defaultScale(20),
                  marginBottom: defaultScale(15),
                  width: '100%',
                }}
              />
              <Text style={styles.strenghtValue}>{visibleStrengthValue}</Text>
            </View>
          </View>
        </View>
      </View>
      <View style={styles.buttonContainer}>
        <Button
          containerStyle={[
            styles.buttonStyle,
            { width: '40%', marginRight: defaultScale(10) },
          ]}
          title={'Back'}
          fontColor={theme.PRIMARY}
          buttonType={ButtonTypes.OUTLINE}
          onPress={getPreviousStep}
        />
        <Button
          containerStyle={[styles.buttonStyle, { flex: 1 }]}
          title={'Save'}
          isLoading={isAssessmentResultLoading}
          onPress={async () => {
            await onSave(strengthValues);
            getPreviousStep();
          }}
          fontColor={theme.WHITE}
        />
      </View>
    </View>
  );
};

const mapStateToProps = (state) => {
  return {
    userStrengthRatios: selectUserStrengthRatios(state),
    isAssessmentResultLoading: selectIsAssessmentResultLoading(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  const userServiceClient = new UserServiceClient(
    getApiConfig(),
    fetch,
    Logger.debug,
    new AuthTokenBuilder(),
  );
  return {
    onSave: (userStrengthRatios: StrengthRatioForMuscleGroups) =>
      dispatch(
        saveManualStrengthAssessment(userServiceClient, userStrengthRatios),
      ),
  };
};

export const ManualSelection = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ManualSelectionComponent);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  bodyGroupCard: {
    width: sliderCardWidth,
    height: defaultScale(80),
    backgroundColor: theme.WHITE,
    marginLeft: sliderCardMargin,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: defaultScale(15),
    borderRadius: defaultScale(6),
    borderColor: '#D6D6D6',
    borderWidth: 1,
  },
  bodyGroupText: {
    fontFamily: theme.Custom700,
    fontSize: scaleText(18),
    textAlign: 'center',
  },
  imageContainer: {
    flex: 1,
  },
  chooseBodyGroupContainer: {
    flexDirection: 'row',
  },
  iconContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: defaultScale(15),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: defaultScale(15),
    marginHorizontal: defaultScale(15),
  },
  buttonStyle: {
    height: defaultScale(45),
  },
  sliderContainer: {
    flexDirection: 'row',
    marginVertical: defaultScale(10),
    paddingHorizontal: defaultScale(40),
  },
  strenghtValue: {
    ...theme.textStyle.small,
  },
});
